import React, { useMemo, useState } from 'react';
import { Info, Sparkles, ChevronDown } from 'lucide-react';

/**
 * Passive, non-blocking warning banner that surfaces when a custom cutline's
 * detected die-line forces a proportional size change.
 *
 * Displays a headline chip ("Actual cut-line area: W×H") plus a compact
 * helper tooltip that turns the backend's silent bbox-shrink optimization
 * into a visible customer-win moment ("We shrank the die-line to your
 * artwork to save material and pack more per sheet").
 *
 * onChangeSize is kept in the props signature for backward compatibility
 * with the parent's wiring, but no longer rendered as a button — this
 * alert is FYI-only; customers can still go back via the wizard if they
 * want to override the auto-fit.
 */
const formatNumber = (n) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
};

const InlineProductionSizeAlert = ({ info }) => {
  const [open, setOpen] = useState(false);

  // Snapshot area numbers in a single pass so the JSX stays readable.
  const {
    prodW,
    prodH,
    reqW,
    reqH,
    areaSavedPct,
  } = useMemo(() => {
    if (!info) return {};
    const pW = info.production_width;
    const pH = info.production_height;
    const rW = info.requested_width;
    const rH = info.requested_height;
    let pct = null;
    // Only surface a "savings" figure when we have all four numbers + the
    // production area is genuinely smaller than the requested one.
    if (
      typeof pW === 'number' && typeof pH === 'number' &&
      typeof rW === 'number' && typeof rH === 'number' &&
      pW > 0 && pH > 0 && rW > 0 && rH > 0
    ) {
      const prodArea = pW * pH;
      const reqArea = rW * rH;
      if (prodArea < reqArea) {
        pct = Math.round((1 - prodArea / reqArea) * 100);
      }
    }
    return {
      prodW: typeof pW === 'number' ? pW.toFixed(4) : '',
      prodH: typeof pH === 'number' ? pH.toFixed(4) : '',
      reqW: rW,
      reqH: rH,
      areaSavedPct: pct,
    };
  }, [info]);

  if (!info) return null;

  return (
    <div
      data-testid="production-size-alert"
      className="p-4 bg-amber-50 border border-amber-200 rounded-xl"
    >
      <div className="flex items-start gap-4">
        <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 text-amber-900 text-sm leading-relaxed">
          <p className="font-semibold mb-2">Heads up — production size adjustment</p>
          <p>
            Based on your artwork, your custom die-line is now{' '}
            <span className="font-mono font-bold">{prodW}" × {prodH}"</span>
            {(reqW || reqH) && (
              <>
                {' '}since the original selected size was{' '}
                <span className="font-mono">{formatNumber(reqW)} × {formatNumber(reqH)}</span>
              </>
            )}.
          </p>

          {/* Chip row — makes the adjusted cut-line area easy to scan at a
              glance + invites customers to tap for the "why". */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              data-testid="cutline-area-chip"
              className="inline-flex items-center gap-1.5 bg-white border border-amber-300 rounded-full px-3 py-1 text-xs font-semibold text-amber-900 shadow-sm"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#4A7C59]" />
              Actual cut-line area:{' '}
              <span className="font-mono">{prodW}" × {prodH}"</span>
            </span>
            {areaSavedPct !== null && (
              <span
                data-testid="cutline-area-savings-chip"
                className="inline-flex items-center gap-1 bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-full px-3 py-1 text-xs font-semibold text-[#2E5A3D]"
                title="Material saved vs. the originally requested bounding box"
              >
                ~{areaSavedPct}% less material
              </span>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="cutline-area-explain-toggle"
              className="inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2"
              aria-expanded={open}
            >
              Why did we shrink it?
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </button>
          </div>

          {open && (
            <p
              data-testid="cutline-area-explainer"
              className="mt-2 text-xs text-amber-900/90 bg-white/60 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed"
            >
              We automatically traced the die-line to hug your artwork as
              closely as is physically possible. That saves material — and
              for Pages products it usually means more stickers packed onto
              each printed sheet, which keeps the per-sticker cost down.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InlineProductionSizeAlert;
